import SoundManager from "../../../../../shared/lib/html5-software-library/src/ranj/audio/SoundManager";
import Button from "../../../../../shared/lib/html5-software-library/src/ranj/components/buttons/Button";
import LanguagePack from "../../../../../shared/lib/html5-software-library/src/ranj/language/LanguagePack";
import StageManager from "../../../../../shared/lib/html5-software-library/src/ranj/performance/StageManager";
import AnimationFactory from "../../../../../shared/lib/html5-software-library/src/ranj/ui/animation/AnimationFactory";
import AnimationOptions from "../../../../../shared/lib/html5-software-library/src/ranj/ui/animation/AnimationOptions";
import AbstractContextObject from "../../../../../shared/src/ranj/app/core/AbstractContextObject";
import AppModel from "../../../../../shared/src/ranj/app/core/AppModel";
import NavigateEvent from "../../../../../shared/src/ranj/app/navigation/NavigateEvent";
import SkinFactory from "../../../../../shared/src/ranj/app/ui/SkinFactory";
import SkinKeys from "../../../../../shared/src/ranj/app/ui/SkinKeys";
import GameModel from "../model/GameModel";
import PuzzleModelEvent from "../model/PuzzleModelEvent";
import TutorialOverlayMediatorEvent from "../model/TutorialOverlayMediatorEvent";
import TutorialPlayerEvent from "../model/TutorialPlayerEvent";
import TutorialStep from "../model/TutorialStep";
import ActorView from "../view/ActorView";
import PuzzleView from "../view/PuzzleView";

export default class TutorialPlayer extends AbstractContextObject {
    /**
     * @constructor
     * @param {soma.EventDispatcher} dispatcher 
     * @param {LanguagePack} languagePack 
     * @param {SoundManager} soundManager 
     * @param {AppModel} appModel 
     * @param {GameModel} gameModel 
     * @param {Object} gameConfig - parsed json (game.json)
     * @param {StageManager} stageManager
     */
    constructor(dispatcher, languagePack, soundManager, appModel, gameModel, gameConfig, stageManager) {
        super(dispatcher, languagePack, soundManager, appModel);

        this._gameModel = gameModel;
        this._gameConfig = gameConfig;
        this._stageManager = stageManager;

        this._puzzleModel = null;
        /** @type {Button} */
        this._rowButton = null;
        /** @type {Button} */
        this._skipButton = null;
        /** @type {ActorView} */
        this._clickableActorView = null;

        /** @type {Array<TutorialStep>} */
        this._steps = [];
        this._currentStep = -1;

        this._mainStage = this._stageManager.getStages()["mainGameCanvas"];

        this._nextStepBound = this.nextStep.bind(this);
    }

    initialize() {
        this._puzzleModel = this._gameModel.initTutorialPuzzle();

        this._initializeSteps();
    }

    reset() {
        this._puzzleModel = null;
        this._rowButton = null;
        this._skipButton = null;
        this._clickableActorView = null;
        this._tutorialOverlayPopup = null;

        // ensure all listeners are removed from the tutorial steps
        for (let i = 0; i < this._steps.length; i++) {
            const step = this._steps[i];
            step.removeEventListener(TutorialStep.COMPLETED, this._nextStepBound);
            step.destroy();
        }

        this._steps = [];
        this._currentStep = -1;
    }

    destroy() {
        this.reset();

        super.destroy();
    }

    get puzzleModel() {
        return this._puzzleModel;
    }

    set rowButton(value) {
        this._rowButton = value;
    }

    set skipButton(value) {
        this._skipButton = value;
    }

    set clickableActorView(value) {
        this._clickableActorView = value;
    }

    start() {
        const animationDelay = this._gameConfig.getValue("tutorial.delay") + PuzzleView.ANIMATE_IN_DURATION;
        const animationDuration = this._gameConfig.getValue("tutorial.fadeDuration");
        const animationOptions = new AnimationOptions(animationDelay, animationDuration, createjs.Ease.quadIn, this._nextStepBound);

        this._tutorialOverlayPopup = SkinFactory.getSkin(SkinKeys.TUTORIAL_OVERLAY_SKIN);
        const commandEvent = new NavigateEvent(NavigateEvent.POP_UP_REQUEST, null, this._tutorialOverlayPopup, undefined, undefined, false, undefined, AnimationFactory.FADE_IN, animationOptions);
        this.dispatch(commandEvent.type, { data: commandEvent });
    }

    replay() {
        // unregister listeners from gatekeeper step
        this.unregisterEventListener(TutorialOverlayMediatorEvent.REPLAY_BUTTON_CLICKED, this._replayBound);
        this.unregisterEventListener(TutorialOverlayMediatorEvent.FINISH_BUTTON_CLICKED, this._tutorialGatekeeperStepCompleteBound);

        const animationDuration = this._gameConfig.getValue("tutorial.fadeDuration");
        const animationOptions = new AnimationOptions(0, animationDuration, createjs.Ease.quadIn, () => {
            this.dispatch(TutorialPlayerEvent.REPLAY);
        });

        const commandEvent = new NavigateEvent(NavigateEvent.CLOSE_POP_UP_REQUEST, null, this._tutorialOverlayPopup, undefined, undefined, false, undefined, AnimationFactory.FADE_OUT, animationOptions);
        this.dispatch(commandEvent.type, { data: commandEvent });
    }

    complete() {
        const animationDuration = this._gameConfig.getValue("tutorial.fadeDuration");
        const animationOptions = new AnimationOptions(0, animationDuration, createjs.Ease.quadIn, () => {
            this.dispatch(TutorialPlayerEvent.COMPLETED);

            this.destroy();
        });

        const commandEvent = new NavigateEvent(NavigateEvent.CLOSE_POP_UP_REQUEST, null, this._tutorialOverlayPopup, undefined, undefined, false, undefined, AnimationFactory.FADE_OUT, animationOptions);
        this.dispatch(commandEvent.type, { data: commandEvent });
    }

    nextStep() {
        if (this._currentStep + 1 >= this._steps.length) {
            this.complete();
            return;
        }

        if (this._currentStep >= 0) {
            const previousStep = this._steps[this._currentStep];
            previousStep.removeEventListener(TutorialStep.COMPLETED, this._nextStepBound);
        }

        const nextStep = this._steps[this._currentStep + 1];
        nextStep.addEventListener(TutorialStep.COMPLETED, this._nextStepBound);

        nextStep.enter();

        this._currentStep++;

        const nextStepEvent = new TutorialPlayerEvent(TutorialPlayerEvent.NEXT_STEP, { stepIndex: this._currentStep });
        this.dispatch(nextStepEvent.type, nextStepEvent);
    }

    _initializeSteps() {
        const step1 = new TutorialStep();
        step1.onEnter = () => {
            this._tutorialStep1CompleteBound = step1.complete.bind(step1);
            this._puzzleModel.addEventListener(PuzzleModelEvent.MOVE, this._tutorialStep1CompleteBound);

            this._clickableActorView.button.setEnabled(true);
        };
        step1.onComplete = () => {
            this._puzzleModel.removeEventListener(PuzzleModelEvent.MOVE, this._tutorialStep1CompleteBound);

            this._clickableActorView.button.setEnabled(false);
        };

        this._steps.push(step1);


        const step2 = new TutorialStep();
        step2.onEnter = () => {
            this._tutorialStep2CompleteBound = step2.complete.bind(step2);
            this._rowButton.addEventListener('click', this._tutorialStep2CompleteBound);

            this._rowButton.setEnabled(true);
        };
        step2.onComplete = () => {
            this._rowButton.removeEventListener('click', this._tutorialStep2CompleteBound);

            this._rowButton.setEnabled(false);
        };

        this._steps.push(step2);


        const step3 = new TutorialStep();
        step3.onEnter = () => {
            this._tutorialStep3CompleteBound = step3.complete.bind(step3);
            this._puzzleModel.addEventListener(PuzzleModelEvent.MOVE, this._tutorialStep3CompleteBound);

            this._clickableActorView.button.setEnabled(true);
        };
        step3.onComplete = () => {
            this._puzzleModel.removeEventListener(PuzzleModelEvent.MOVE, this._tutorialStep3CompleteBound);

            this._clickableActorView.button.setEnabled(false);
        };

        this._steps.push(step3);

        this._steps.push(this._constructNonInteractiveTutorialStep());
        this._steps.push(this._constructNonInteractiveTutorialStep());
        this._steps.push(this._constructNonInteractiveTutorialStep());


        const skipButtonStep = new TutorialStep();
        skipButtonStep.onEnter = () => {
            this._tutorialSkipButtonStepCompleteBound = skipButtonStep.complete.bind(skipButtonStep);
            this._skipButton.addEventListener('click', this._tutorialSkipButtonStepCompleteBound);

            this._skipButton.setEnabled(true);
        };
        skipButtonStep.onComplete = () => {
            this._skipButton.removeEventListener('click', this._tutorialSkipButtonStepCompleteBound);

            this._skipButton.setEnabled(false);
        };

        this._steps.push(skipButtonStep);


        const gateKeeperStep = new TutorialStep();
        gateKeeperStep.onEnter = () => {
            this._tutorialGatekeeperStepCompleteBound = gateKeeperStep.complete.bind(gateKeeperStep);
            this._replayBound = this.replay.bind(this);

            this.registerEventListener(TutorialOverlayMediatorEvent.REPLAY_BUTTON_CLICKED, this._replayBound);
            this.registerEventListener(TutorialOverlayMediatorEvent.FINISH_BUTTON_CLICKED, this._tutorialGatekeeperStepCompleteBound);
        };
        gateKeeperStep.onComplete = () => {
            this.unregisterEventListener(TutorialOverlayMediatorEvent.REPLAY_BUTTON_CLICKED, this._replayBound);
            this.unregisterEventListener(TutorialOverlayMediatorEvent.FINISH_BUTTON_CLICKED, this._tutorialGatekeeperStepCompleteBound);
        };

        this._steps.push(gateKeeperStep);
    }

    _constructNonInteractiveTutorialStep() {
        const step = new TutorialStep();
        step.onEnter = () => {
            this._tutorialStepCompleteBound = step.complete.bind(step);

            this._mainStage.addEventListener("mousedown", this._tutorialStepCompleteBound);
        };
        step.onComplete = () => {
            this._mainStage.removeEventListener("mousedown", this._tutorialStepCompleteBound);
        };

        return step;
    }
}